/**
 * Props for the SignupStep component.
 */
interface SignupStepProps {
  /** Step number shown inside the orange-bordered circle (1-based). */
  number: number;
  /** Localized "Step" label used to build the screen-reader aria-label. */
  stepLabel: string;
  /** Step heading, e.g. "Enter your email address". */
  title: string;
  /** Step body copy. */
  body: string;
}

/**
 * A single numbered step card in the signup walkthrough.
 * Number circle sits to the left of the text on tablet/desktop and
 * stacks above the text on mobile.
 */
export function SignupStep({ number, stepLabel, title, body }: SignupStepProps) {
  return (
    <article
      className="bg-white rounded-[14px] p-[18px] sm:p-6 flex flex-col sm:flex-row gap-5 sm:gap-6"
      style={{
        border: '0.5px solid rgba(36,36,36,0.12)',
      }}
    >
      {/* Decorative numbered circle */}
      <div className="shrink-0 flex justify-center sm:justify-start">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            backgroundColor: '#fff5e8',
            border: '1.5px solid #ff7e1a',
          }}
          aria-label={`${stepLabel} ${String(number)}`}
        >
          <span
            className="text-[20px] font-bold text-[#ff7e1a]"
            style={{ fontFamily: '"Urbanist", "Helvetica", sans-serif' }}
            aria-hidden="true"
          >
            {number}
          </span>
        </div>
      </div>

      {/* Text content */}
      <div className="flex-1 text-center sm:text-left">
        <h3
          className="text-[19px] font-semibold text-[#242424]"
          style={{ fontFamily: '"Urbanist", "Helvetica", sans-serif' }}
        >
          {title}
        </h3>
        <p
          className="mt-2 text-[15px] text-[#5F5E5A]"
          style={{
            fontFamily: '"Urbanist", "Helvetica", sans-serif',
            lineHeight: 1.6,
          }}
        >
          {body}
        </p>
      </div>
    </article>
  );
}
